/* Portfolio demo: in-memory API — no network, no backend */

const sleep = (ms = 60) => new Promise((r) => setTimeout(r, ms));

function notFound() {
  const e = new Error("Not found");
  e.status = 404;
  throw e;
}

let nextId = 500;

const DEMO_USER_ID = 1;

const floors = [
  { id: 1, name: "Floor 1" },
  { id: 2, name: "Floor 2" },
  { id: 3, name: "Floor 1 Split" },
];

let resources = [
  {
    id: 101,
    name: "P-A01",
    type_name: "Parking",
    floor_id: 1,
    floor_name: "Floor 1",
    is_active: true,
    description: "",
    position_x: 0,
    position_y: 0,
  },
  {
    id: 102,
    name: "M001",
    type_name: "Motorcycle",
    floor_id: 1,
    floor_name: "Floor 1",
    is_active: true,
    description: "",
    position_x: 0,
    position_y: 0,
  },
  {
    id: 201,
    name: "D101",
    type_name: "Desk",
    floor_id: 1,
    floor_name: "Floor 1",
    is_active: true,
    description: "",
    position_x: 120,
    position_y: 200,
  },
  {
    id: 202,
    name: "D102",
    type_name: "Desk",
    floor_id: 1,
    floor_name: "Floor 1",
    is_active: true,
    description: "",
    position_x: 180,
    position_y: 200,
  },
];

let users = [
  {
    id: DEMO_USER_ID,
    username: "Demo",
    email: "demo@example.com",
    role: "admin",
    created_at: new Date().toISOString(),
  },
  {
    id: 2,
    username: "user2",
    email: "user2@example.com",
    role: "user",
    created_at: new Date().toISOString(),
  },
];

function jakartaNowParts() {
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
  const jkt = new Date(utcMs + 7 * 3_600_000);
  const p = (n) => String(n).padStart(2, "0");
  return {
    y: jkt.getFullYear(),
    m: p(jkt.getMonth() + 1),
    d: p(jkt.getDate()),
    H: p(jkt.getHours()),
    M: p(jkt.getMinutes()),
    S: p(jkt.getSeconds()),
  };
}

function jktSqlRange() {
  const { y, m, d, H, M, S } = jakartaNowParts();
  const day = `${y}-${m}-${d}`;
  return {
    start: `${day} ${H}:${M}:${S}`,
    end: `${day} 23:59:59`,
    prev: `${day} 08:00:00`,
  };
}

const { start: activeStart, end: activeEnd, prev: activePrev } = jktSqlRange();

let bookings = [
  {
    id: 401,
    resource_id: 201,
    resource_name: "D101",
    user_id: DEMO_USER_ID,
    username: "Demo",
    status: "confirmed",
    start_local: activePrev,
    end_local: activeEnd,
    start_time: activePrev,
    end_time: activeEnd,
    type_name: "desk",
    type: "desk",
    notes: "Demo booking",
  },
  {
    id: 402,
    resource_id: 101,
    resource_name: "P-A01",
    user_id: 2,
    username: "user2",
    status: "confirmed",
    start_local: activeStart,
    end_local: activeEnd,
    start_time: activeStart,
    end_time: activeEnd,
    type_name: "parking",
    type: "parking",
    notes: "",
  },
];

let commutePrefs = {
  commute: "Motor",
  estimated_distance: "12 km",
};

const slackStore = {
  slack: { webhook_url: "", channel: "", enabled: false },
  "slack-reminder": {
    channel: "",
    enabled: true,
    message: "",
    times: ["08:00"],
    weekdays: [1, 2, 3, 4, 5],
  },
  "slack-daily": { enabled: true },
};

let commutesAdmin = [];

function parsePath(url) {
  const [path, qs] = String(url).split("?");
  const params = {};
  if (qs) {
    new URLSearchParams(qs).forEach((v, k) => {
      params[k] = v;
    });
  }
  return { path, params };
}

function matchBookingsParams(query) {
  let list = [...bookings];
  if (query.mine === "1" || query.mine === 1) {
    list = list.filter((b) => b.user_id === DEMO_USER_ID);
  }
  if (query.user_only === "true" || query.user_only === true) {
    list = list.filter((b) => b.user_id === DEMO_USER_ID);
  }
  if (query.start && query.end) {
    const s = query.start;
    const e = query.end;
    list = list.filter((b) => {
      const bs = (b.start_local || b.start_time || "").slice(0, 19);
      const be = (b.end_local || b.end_time || "").slice(0, 19);
      return bs && be && bs < e && be > s;
    });
  }
  return list;
}

async function handle(method, url, body, configParams = {}) {
  await sleep();
  const merged = { ...parsePath(url).params, ...configParams };
  const path = url.split("?")[0];

  /* -------- Auth (optional; login uses AuthContext mock) -------- */
  if (method === "post" && path === "/auth/login") {
    return {
      token: "demo-token",
      user: users.find((u) => u.username === body?.username) || users[0],
    };
  }
  if (method === "post" && path === "/auth/signup") {
    if (body?.token && body?.user) return { token: body.token, user: body.user };
    return { message: "Account created (demo)." };
  }
  if (method === "post" && path === "/auth/forgot-password") {
    return {
      message: "Reset link (demo): #",
      reset_url: `${window.location.origin}/demo/booking/reset-password?token=demo`,
    };
  }
  if (method === "post" && path === "/auth/reset-password") {
    return { message: "Password updated (demo)." };
  }

  /* -------- Dashboard -------- */
  if (method === "get" && path === "/dashboard/stats") {
    const parkingBookings = bookings.filter((b) => (b.type_name || "").toLowerCase() === "parking");
    const motorcycleBookings = bookings.filter((b) => (b.type_name || "").toLowerCase() === "motorcycle");
    const deskBookings = bookings.filter((b) => (b.type_name || "").toLowerCase() === "desk");
    const countRes = (t) => resources.filter((r) => r.is_active !== false && r.type_name === t).length;
    const pt = countRes("Parking") || 6;
    const mt = countRes("Motorcycle") || 8;
    const dt = countRes("Desk") || 24;
    const activeIds = (type) => {
      const now = jktSqlRange().start;
      return new Set(
        bookings
          .filter((b) => (b.type_name || "").toLowerCase() === type.toLowerCase())
          .filter((b) => {
            const s = (b.start_local || b.start_time || "").slice(0, 19);
            const e = (b.end_local || b.end_time || "").slice(0, 19);
            return s.length === 19 && e.length === 19 && s <= now && now <= e;
          })
          .map((b) => b.resource_id)
      );
    };
    const pa = Math.max(0, pt - activeIds("parking").size);
    const ma = Math.max(0, mt - activeIds("motorcycle").size);
    const da = Math.max(0, dt - activeIds("desk").size);
    return {
      parkingBookings,
      motorcycleBookings,
      deskBookings,
      parking: { total: pt, available: pa },
      motorcycle: { total: mt, available: ma },
      desk: { total: dt, available: da },
    };
  }

  /* -------- Resources (user) -------- */
  if (method === "get" && path === "/resources") {
    const t = (merged.type || "").toLowerCase();
    const map = { desk: "Desk", parking: "Parking", motorcycle: "Motorcycle" };
    const want = map[t] || merged.type;
    const arr = resources.filter(
      (r) => r.is_active !== false && (want ? r.type_name === want : true)
    );
    return arr;
  }

  if (method === "get" && path === "/floors") {
    return [...floors];
  }

  if (method === "get" && path === "/admin/resources") {
    let arr = [...resources];
    if (merged.type) {
      const w = merged.type;
      arr = arr.filter((r) => (r.type_name || "").toLowerCase() === String(w).toLowerCase());
    }
    return arr;
  }

  if (method === "post" && path === "/admin/resources") {
    const id = ++nextId;
    const floor = floors.find((f) => String(f.id) === String(body.floor_id));
    const row = {
      id,
      name: body.name,
      type_name: body.type_name || body.type || "Desk",
      floor_id: body.floor_id || null,
      floor_name: floor?.name || body.floor_name || "",
      is_active: body.is_active !== false,
      description: body.description || "",
      position_x: body.position_x ?? 0,
      position_y: body.position_y ?? 0,
    };
    resources.push(row);
    return row;
  }

  if (method === "put" && /^\/admin\/resources\/[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    const idx = resources.findIndex((r) => String(r.id) === id);
    if (idx === -1) notFound();
    resources[idx] = { ...resources[idx], ...body, id: resources[idx].id };
    return resources[idx];
  }

  if (method === "delete" && /^\/admin\/resources\/[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    resources = resources.filter((r) => String(r.id) !== id);
    return { ok: true };
  }

  /* -------- Bookings -------- */
  if (method === "get" && path === "/bookings") {
    return matchBookingsParams(merged);
  }

  if (method === "post" && path === "/bookings") {
    const id = ++nextId;
    const resMeta = resources.find((r) => String(r.id) === String(body.resource_id));
    const row = {
      id,
      resource_id: body.resource_id,
      resource_name: resMeta?.name || body.resource_name || "",
      user_id: DEMO_USER_ID,
      username: "Demo",
      status: body.status || "confirmed",
      start_local: body.start_local || body.start_time,
      end_local: body.end_local || body.end_time,
      start_time: body.start_local || body.start_time,
      end_time: body.end_local || body.end_time,
      type_name: (body.type_name || body.type || "desk").toLowerCase(),
      type: (body.type_name || body.type || "desk").toLowerCase(),
      notes: body.notes || "",
      estimated_distance: body.estimated_distance,
    };
    bookings.push(row);
    return row;
  }

  if (method === "put" && /^\/bookings\/[^/]+$/.test(path) && !path.endsWith("/release")) {
    const id = path.split("/").pop();
    const idx = bookings.findIndex((b) => String(b.id) === id);
    if (idx === -1) notFound();
    bookings[idx] = { ...bookings[idx], ...body, id: bookings[idx].id };
    return bookings[idx];
  }

  if (method === "delete" && /^\/bookings\/[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    bookings = bookings.filter((b) => String(b.id) !== id);
    return { ok: true };
  }

  if (method === "put" && /\/bookings\/[^/]+\/release$/.test(path)) {
    const id = path.match(/\/bookings\/([^/]+)\/release/)?.[1];
    const idx = bookings.findIndex((b) => String(b.id) === id);
    if (idx !== -1) bookings[idx] = { ...bookings[idx], status: "released" };
    return { ok: true };
  }

  /* -------- My commute -------- */
  if (method === "get" && path === "/my/commute") {
    return { ...commutePrefs };
  }

  if (method === "put" && path === "/my/commute") {
    commutePrefs = {
      commute: body.commute ?? null,
      estimated_distance: body.estimated_distance ?? null,
    };
    return { ...commutePrefs };
  }

  if (method === "delete" && path === "/my/commute") {
    commutePrefs = { commute: null, estimated_distance: null };
    return {};
  }

  /* -------- Admin users -------- */
  if (method === "get" && (path === "/admin/users" || path === "/users")) {
    return users.map((u) => ({ ...u }));
  }

  if (method === "post" && (path === "/admin/users" || path === "/users")) {
    const id = ++nextId;
    const row = {
      id,
      username: body.username,
      email: body.email || "",
      role: body.role === "admin" ? "admin" : "user",
      created_at: new Date().toISOString(),
    };
    users.push(row);
    return row;
  }

  if (method === "put" && /^(\/admin\/users\/|\/users\/)[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    const idx = users.findIndex((u) => String(u.id) === id);
    if (idx === -1) notFound();
    users[idx] = { ...users[idx], ...body, id: users[idx].id };
    if (body.role) users[idx].role = body.role === "admin" ? "admin" : "user";
    return users[idx];
  }

  if (method === "delete" && /^(\/admin\/users\/|\/users\/)[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    if (String(id) !== String(DEMO_USER_ID)) {
      users = users.filter((u) => String(u.id) !== id);
    }
    return { ok: true };
  }

  /* -------- Admin bookings / commutes -------- */
  if (method === "get" && path === "/admin/commutes") {
    return [...commutesAdmin];
  }

  if (method === "delete" && /^\/admin\/commutes\/[^/]+$/.test(path)) {
    const uid = path.split("/").pop();
    commutesAdmin = commutesAdmin.filter((r) => String(r.user_id) !== uid);
    return { ok: true };
  }

  if (method === "delete" && /^\/admin\/bookings\/[^/]+$/.test(path)) {
    const id = path.split("/").pop();
    bookings = bookings.filter((b) => String(b.id) !== id);
    return { ok: true };
  }

  if (method === "put" && /\/admin\/bookings\/[^/]+\/cancel$/.test(path)) {
    const id = path.match(/\/admin\/bookings\/([^/]+)\/cancel/)?.[1];
    const idx = bookings.findIndex((b) => String(b.id) === id);
    if (idx !== -1) bookings[idx] = { ...bookings[idx], status: "cancelled" };
    return bookings[idx] || { ok: true };
  }

  if (method === "put" && /^\/admin\/bookings\/[^/]+$/.test(path) && !path.includes("/cancel") && !path.includes("/release")) {
    const id = path.split("/").pop();
    const idx = bookings.findIndex((b) => String(b.id) === id);
    if (idx !== -1) bookings[idx] = { ...bookings[idx], ...body };
    return bookings[idx];
  }

  if (method === "put" && /\/admin\/bookings\/[^/]+\/release$/.test(path)) {
    const id = path.match(/\/admin\/bookings\/([^/]+)\/release/)?.[1];
    const idx = bookings.findIndex((b) => String(b.id) === id);
    if (idx !== -1) bookings[idx] = { ...bookings[idx], status: "released" };
    return { ok: true };
  }

  /* -------- Slack / integrations -------- */
  if (method === "get" && path === "/admin/settings/slack") {
    return { ...slackStore.slack };
  }
  if (method === "get" && path === "/admin/settings/slack-reminder") {
    return { ...slackStore["slack-reminder"] };
  }
  if (method === "get" && path === "/admin/settings/slack-daily") {
    return { ...slackStore["slack-daily"] };
  }
  if (method === "post" && path === "/admin/settings/slack") {
    slackStore.slack = { ...slackStore.slack, ...body };
    return slackStore.slack;
  }
  if (method === "post" && path === "/admin/settings/slack-reminder") {
    slackStore["slack-reminder"] = { ...slackStore["slack-reminder"], ...body };
    return slackStore["slack-reminder"];
  }
  if (method === "post" && path.startsWith("/admin/slack")) {
    return { ok: true, demo: true };
  }

  console.warn("[booking mock API] unhandled", method, url);
  return null;
}

const wrap = (p) =>
  p.then((data) => ({ data })).catch((e) => {
    const err = new Error(e?.message || "Request failed");
    const status = e?.status || 400;
    err.response = { data: { error: err.message }, status };
    return Promise.reject(err);
  });

export const api = {
  get: (url, config) => wrap(handle("get", url, null, config?.params)),
  post: (url, data, config) => wrap(handle("post", url, data, config?.params)),
  put: (url, data, config) => wrap(handle("put", url, data, config?.params)),
  delete: (url, config) => wrap(handle("delete", url, null, config?.params)),
};

export function setAuthToken(token) {
  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
}

const saved = localStorage.getItem("token");
if (saved) setAuthToken(saved);
